import { create } from 'zustand'
import type { BlockValidateResponse } from '@/services/block-api-service'
import type {
  PreviewPanel,
  ValidationState,
  VisualDiffResult,
  InteractionStepResult,
  ScenarioRunResult,
  ActionFlowTraceEntry,
} from '../types'
import {
  isWriterProducedPixelEvidenceReceipt,
  type MeasuredPixelEvidenceReceipt,
} from '../services/measured-pixel-evidence'

export interface PreviewTestingStoreState {
  // Validation
  validationResults: Record<string, BlockValidateResponse>
  validationScore: number | null
  validationState: ValidationState
  /** Project the current validation results belong to (staleness guard). */
  validatedProjectId: string | null
  /**
   * The exact currentProject reference the results were computed from.
   * Mutations made while the badge is unmounted change the reference; on
   * remount a mismatch means the score is stale and must re-validate.
   */
  validatedProjectRef: unknown

  // Panel
  activePanel: PreviewPanel | null

  // Visual diff
  diffResult: VisualDiffResult | null
  referenceImage: string | null
  measuredPixelEvidenceReceipts: MeasuredPixelEvidenceReceipt[]

  // Interaction tests
  interactionResults: InteractionStepResult[]

  // interaction scenarios
  scenarioResults: ScenarioRunResult[]

  // Runtime ActionFlow traces (real preview runs — the bounded-12 buffer the
  // verification badge, live overlay, inspector, and chat bubble all read).
  actionFlowTraces: ActionFlowTraceEntry[]

  /**
   * The latest editor Run-(dry) result, kept in its OWN single-latest slot —
   * SEPARATE from actionFlowTraces. A dry-run is an off-canvas author probe
   * (empty context, side effects suppressed); it must never (a) evict a real
   * preview trace from the bounded-12 buffer and regress the Verified badge,
   * nor (b) leak into the live overlay/inspector/chat bubble (which read
   * actionFlowTraces only). The dry-run results PANEL reads this slot instead.
   */
  dryRunTrace: ActionFlowTraceEntry | null

  // Failed blocks (for red border highlight)
  failedBlocks: string[]

  // Cross-block issues (contract stage 7)
  crossBlockIssues: Array<{ stage: number; level: string; message: string }>

  // Actions
  setValidationResults: (results: Record<string, BlockValidateResponse>) => void
  setValidationState: (state: ValidationState) => void
  setActivePanel: (panel: PreviewPanel) => void
  setDiffResult: (result: VisualDiffResult | null) => void
  setReferenceImage: (dataUrl: string | null) => void
  addMeasuredPixelEvidenceReceipts: (
    receipts: readonly MeasuredPixelEvidenceReceipt[],
  ) => void
  setInteractionResults: (results: InteractionStepResult[]) => void
  setScenarioResults: (results: ScenarioRunResult[]) => void
  addActionFlowTrace: (trace: ActionFlowTraceEntry) => void
  clearActionFlowTraces: () => void
  /** Write the latest dry-run result into its dedicated slot (NOT the shared
   *  preview buffer) — keeps dry-runs out of badge/overlay/inspector/chat. */
  setDryRunTrace: (trace: ActionFlowTraceEntry | null) => void
  /** RUX-05: drop the traces matching a predicate (flow edited/renamed/
   *  deleted) so verification badges can never claim a stale run. */
  removeActionFlowTraces: (predicate: (trace: ActionFlowTraceEntry) => boolean) => void
  setFailedBlocks: (blocks: string[]) => void
  setCrossBlockIssues: (issues: Array<{ stage: number; level: string; message: string }>) => void
  setValidatedProjectId: (projectId: string | null) => void
  setValidatedProjectRef: (ref: unknown) => void
  /** Clear only validation outputs (project switch) — keeps diffs/traces. */
  clearValidation: () => void
  reset: () => void
}

const initialState = {
  validationResults: {} as Record<string, BlockValidateResponse>,
  validationScore: null as number | null,
  validationState: 'idle' as ValidationState,
  validatedProjectId: null as string | null,
  validatedProjectRef: null as unknown,
  activePanel: null as PreviewPanel | null,
  diffResult: null as VisualDiffResult | null,
  referenceImage: null as string | null,
  measuredPixelEvidenceReceipts: [] as MeasuredPixelEvidenceReceipt[],
  interactionResults: [] as InteractionStepResult[],
  scenarioResults: [] as ScenarioRunResult[],
  actionFlowTraces: [] as ActionFlowTraceEntry[],
  dryRunTrace: null as ActionFlowTraceEntry | null,
  failedBlocks: [] as string[],
  crossBlockIssues: [] as Array<{ stage: number; level: string; message: string }>,
}

export const usePreviewTestingStore = create<PreviewTestingStoreState>()((set, _get) => ({
  ...initialState,

  setValidationResults: (results) => {
    // Local-fallback results carry no real score (degraded partial checks) —
    // average server scores only, mirroring validation-tab/spec-compliance-tab.
    // null → ValidationBadge/TestingPanel render their unknown ('--') state.
    const entries = Object.values(results).filter((r) => r.source !== 'local-fallback')
    const score =
      entries.length > 0
        ? Math.round(entries.reduce((sum, r) => sum + r.score, 0) / entries.length)
        : null

    set({
      validationResults: results,
      validationScore: score,
      validationState: 'done',
    })
  },

  setValidationState: (validationState) => set({ validationState }),

  setActivePanel: (panel) =>
    set((state) => ({
      activePanel: state.activePanel === panel ? null : panel,
    })),

  setDiffResult: (diffResult) => set({ diffResult }),

  setReferenceImage: (referenceImage) => set({ referenceImage }),

  addMeasuredPixelEvidenceReceipts: (receipts) => set((state) => {
    const admitted = receipts.filter(isWriterProducedPixelEvidenceReceipt)
    if (admitted.length === 0) return state
    const replacedKeys = new Set(admitted.map((receipt) => (
      `${receipt.projectId}\u0000${receipt.projectRevision}\u0000${receipt.evidenceRef}`
    )))
    return {
      measuredPixelEvidenceReceipts: [
        ...admitted,
        ...state.measuredPixelEvidenceReceipts.filter((receipt) => !replacedKeys.has(
          `${receipt.projectId}\u0000${receipt.projectRevision}\u0000${receipt.evidenceRef}`,
        )),
      ].slice(0, 50),
    }
  }),

  setInteractionResults: (interactionResults) => set({ interactionResults }),

  setScenarioResults: (scenarioResults) => set({ scenarioResults }),

  addActionFlowTrace: (trace) =>
    set((state) => ({
      actionFlowTraces: [trace, ...state.actionFlowTraces].slice(0, 12),
    })),

  clearActionFlowTraces: () => set({ actionFlowTraces: [] }),

  setDryRunTrace: (dryRunTrace) => set({ dryRunTrace }),

  removeActionFlowTraces: (predicate) =>
    set((state) => ({
      actionFlowTraces: state.actionFlowTraces.filter((trace) => !predicate(trace)),
    })),

  setFailedBlocks: (failedBlocks) => set({ failedBlocks }),

  setCrossBlockIssues: (crossBlockIssues) => set({ crossBlockIssues }),

  setValidatedProjectId: (validatedProjectId) => set({ validatedProjectId }),

  setValidatedProjectRef: (validatedProjectRef) => set({ validatedProjectRef }),

  clearValidation: () =>
    set({
      validationResults: {},
      validationScore: null,
      validationState: 'idle',
      validatedProjectId: null,
      validatedProjectRef: null,
      failedBlocks: [],
    }),

  reset: () => set({ ...initialState }),
}))

if (typeof window !== 'undefined' && import.meta.env.VITE_E2E_MOCK === 'true') {
  ;(window as unknown as { __COGO_PREVIEW_TESTING__?: typeof usePreviewTestingStore })
    .__COGO_PREVIEW_TESTING__ = usePreviewTestingStore
}
